import React, {useEffect, useRef, useState} from "react";
import style from './Stopwatch.module.scss';

const MAX_SPLITS = 60;

const getSplitsPerColumn = (totalSplits) => {
    if (totalSplits > 30) return 20;
    return 10;
}

export default function Stopwatch() {
    const [laps, setLaps] = useState([]);
    const [splitCount, setSplitCount] = useState(0);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [run, setRun] = useState(false);
    const timerRef = useRef();
    const startTimeRef = useRef(0);
    const lapsRef = useRef();

    const totalMs = Math.floor(elapsedMs);
    const milliseconds = totalMs % 1000;
    const totalSeconds = Math.floor(totalMs / 1000);
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);

    // Add leading Zero
    const alz = (n) => {
        if (n < 10) return '0' + n;
        return n
    }

    // Add leading Zero milliseconds
    const alzm = (n) => {
        if (n < 10) return '00' + n;
        else if (n < 100) return '0' + n;
        return n
    }

    const resetTimer = () => {
        // setRun(false);
        startTimeRef.current = performance.now();
        setElapsedMs(0);
        setLaps([]);
        setSplitCount(0);
    }

    const saveSplitTime = () => {
        const time = `${alz(hours)}:${alz(minutes)}:${alz(seconds)}.${alzm(milliseconds)}`;
        setLaps([time, ...laps].slice(0, MAX_SPLITS));
        setSplitCount(splitCount + 1);
    }

    useEffect(() => {
        timerRef.current.focus()
    }, [])

    // Newest splits are prepended to the list, but the scroll container keeps
    // its previous scrollTop/scrollLeft — without this, new splits appear
    // above/before the visible area instead of where the user is looking.
    useEffect(() => {
        if (!lapsRef.current) return
        lapsRef.current.scrollTop = 0
        lapsRef.current.scrollLeft = 0
    }, [laps])

    // Recompute elapsed time from an absolute timestamp each tick, rather than
    // accumulating a fixed +10ms per tick, so setTimeout/render jitter can't drift the clock.
    useEffect(() => {
        if (!run) return;
        startTimeRef.current = performance.now() - elapsedMs;
        const interval = setInterval(() => {
            setElapsedMs(performance.now() - startTimeRef.current);
        }, 10)
        return () => clearInterval(interval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run])

    function handleKeyUp(e) {
        switch (e.key.toLowerCase()) {
            case 'enter':
                setRun(!run);
                break;
            case ' ':
                saveSplitTime();
                break;
            case 'r':
                resetTimer();
                break;
            default:
                break;
        }
    }

    const splitsPerColumn = getSplitsPerColumn(laps.length);
    const lapColumns = [];
    for (let i = 0; i < laps.length; i += splitsPerColumn) {
        lapColumns.push(laps.slice(i, i + splitsPerColumn));
    }

    return (
        <div ref={timerRef} className={style.timer} aria-selected={"true"} tabIndex={0} onKeyPress={handleKeyUp}>
            <h1 className={style.title}>Stopwatch</h1>
            <div className={style.container}>
                <div
                    className={style.digits}>{`${alz(hours)}:${alz(minutes)}:${alz(seconds)}`}
                    <small>.{`${alz(Math.floor(milliseconds / 10))}`}</small></div>
            </div>
            <div className={style.buttons}>
                <button
                    className={`${run ? style.red : style.green}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setRun(!run)}>
                    Start / Stop
                    <kbd className={style.shortcut}>Enter</kbd>
                </button>
                <button
                    className={style.split}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => saveSplitTime()}>
                    Split
                    <kbd className={style.shortcut}>Space</kbd>
                </button>
                <button
                    className={style.reset}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => resetTimer()}>
                    Reset
                    <kbd className={style.shortcut}>R</kbd>
                </button>
            </div>
            <div className={style.laps} ref={lapsRef}>
                {lapColumns.map((column, columnIndex) => (
                    <ul className={style.lapList} key={columnIndex}>
                        {column.map((time, indexInColumn) => {
                            const overallIndex = columnIndex * splitsPerColumn + indexInColumn;
                            const splitNumber = splitCount - overallIndex;
                            const isLatest = overallIndex === 0;
                            return (
                                <li
                                    className={`${style.lap} ${isLatest ? style.latest : ''}`}
                                    key={overallIndex}>
                                    Split {splitNumber}: <b>{time}</b>
                                </li>
                            );
                        })}
                    </ul>
                ))}
            </div>
        </div>
    )

}